import React, { useEffect, useState } from 'react'
import { ImageOff } from 'lucide-react'

// generic component for rendering an image whose URL is served via a
// short-lived signed token. caller passes signFn() which does the
// auth'd POST .../sign request and returns the full URL with the
// HMAC token baked in. we render <img src={signedUrl}> directly so
// the browser uses its native fetch (no XHR-then-blob round-trip).
//
// on error: small placeholder block with an off-image icon. the 5min
// token lifetime is short enough that long-lived components may need
// to re-mount or re-sign. Phase B keeps it simple, signs on first
// mount only. if the image fails to load after token expiry the user
// can refresh the page.

interface SignedImageProps {
  signFn:    () => Promise<string>
  alt:       string
  className?: string
  onError?:  (err: Error) => void
}

export const SignedImage: React.FC<SignedImageProps> = ({ signFn, alt, className, onError }) => {
  const [url,   setUrl]   = useState<string | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    setUrl(null)
    setError(null)
    setLoaded(false)
    signFn()
      .then(u => { if (!cancelled) setUrl(u) })
      .catch(e => {
        if (cancelled) return
        const err = e instanceof Error ? e : new Error(String(e))
        setError(err)
        onError?.(err)
      })
    return () => { cancelled = true }
  }, [signFn, onError])

  if (error) {
    return (
      <div className={`flex items-center gap-2 text-xs text-rose-500 bg-rose-50/40 dark:bg-rose-900/10 border border-rose-200 dark:border-rose-800 rounded p-3 ${className ?? ''}`}>
        <ImageOff className="h-4 w-4" />
        Failed to load image
      </div>
    )
  }
  if (!url) {
    return <div className={`animate-pulse bg-slate-200 dark:bg-slate-700 h-32 rounded ${className ?? ''}`} />
  }
  return (
    <>
      {!loaded && <div className={`animate-pulse bg-slate-200 dark:bg-slate-700 h-32 rounded ${className ?? ''}`} />}
      <img
        src={url}
        alt={alt}
        onLoad={() => setLoaded(true)}
        onError={() => { const e = new Error('image failed to render'); setError(e); onError?.(e) }}
        className={className}
        style={loaded ? undefined : { display: 'none' }}
      />
    </>
  )
}
